import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  Document,
  isSharedSliceContent,
  SharedSliceContent,
} from "@prismicio/types-internal/lib/content"
import type {
  SharedSlice,
  Variation,
} from "@prismicio/types-internal/lib/customtypes"
import { z } from "zod"

import type { SliceConfigWithSharedSlice } from "../../document/LeftSidePanel/sliceTable/sliceTableData"
import { env } from "../env"
import { socketRequest } from "./ws"

interface CreateSlicesArgs {
  uuid: string
  slices: SliceConfigWithSharedSlice[]
  document?: Document
  prompt: string
  locale?: string
  onMessage: (message: CreateSlicesMessage) => void
}

export function createSlices(args: CreateSlicesArgs) {
  const {
    uuid,
    slices: sliceConfigs,
    document,
    prompt,
    locale,
    onMessage,
  } = args

  const slices = sliceConfigs.map(({ sliceConfig }) => sliceConfig)
  const schema = getCreateSlicesSchema({ slices })

  const wsUrl = new URL(env.PRISMIC_AI_SERVICE_WEBSOCKET_ENDPOINT)
  wsUrl.searchParams.set("uuid", uuid)

  socketRequest({
    url: wsUrl,
    onOpen: () =>
      request(new URL("create-slices", env.PRISMIC_AI_SERVICE_BASE_URL), {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, slices, document, prompt, locale },
        okType: z.unknown(),
      }),
    schema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "filling":
        case "slicing":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

function getCreateSlicesSchema({ slices }: { slices: SharedSlice[] }) {
  const [firstSliceId, ...restSliceIds] = slices.map(({ id }) => id)

  if (firstSliceId === undefined)
    throw new Error("At least one slice is required.")

  const sliceIdSchema = z.enum([firstSliceId, ...restSliceIds])

  return z.discriminatedUnion("status", [
    z.object({ status: z.literal("slicing") }),
    z.object({
      status: z.literal("filling"),
      slice: sliceIdSchema,
    }),
    z.object({
      status: z.literal("done"),
      slice: sliceIdSchema,
      slices: z
        .array(z.custom<SharedSliceContent>(isSharedSliceContent))
        .nonempty(),
      cost: z.number(),
    }),
    z.object({ status: z.literal("failed") }),
  ])
}

type CreateSlicesMessage = z.infer<ReturnType<typeof getCreateSlicesSchema>>

interface RefineSliceArgs {
  uuid: string
  document?: Document
  variation: Variation
  content: SharedSliceContent
  prompt: string
  locale?: string
  onMessage: (message: RefineSliceMessage) => void
}

export function refineSlice(args: RefineSliceArgs) {
  const { uuid, document, variation, content, prompt, locale, onMessage } = args

  const wsUrl = new URL(env.PRISMIC_AI_SERVICE_WEBSOCKET_ENDPOINT)
  wsUrl.searchParams.set("uuid", uuid)

  socketRequest({
    url: wsUrl,
    onOpen: () =>
      request(new URL("refine-slice", env.PRISMIC_AI_SERVICE_BASE_URL), {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, document, variation, content, prompt, locale },
        okType: z.unknown(),
      }),
    schema: refineSliceMessageSchema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "refining":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

const refineSliceMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("refining") }),
  z.object({
    status: z.literal("done"),
    slice: z.custom<SharedSliceContent>(isSharedSliceContent),
    cost: z.number(),
  }),
  z.object({ status: z.literal("failed") }),
])

type RefineSliceMessage = z.infer<typeof refineSliceMessageSchema>

interface FitHtmlIntoSliceArgs {
  uuid: string
  slices: SharedSlice[]
  slice: string
  variation: string
  html: string
  onMessage: (message: FitHtmlIntoSliceMessage) => void
}

export function fitHtmlIntoSlice(args: FitHtmlIntoSliceArgs) {
  const { uuid, slices, slice, variation, html, onMessage } = args

  const wsUrl = new URL(env.PRISMIC_AI_SERVICE_WEBSOCKET_ENDPOINT)
  wsUrl.searchParams.set("uuid", uuid)

  socketRequest({
    url: wsUrl,
    onOpen: () =>
      request(new URL("fit-slice", env.PRISMIC_AI_SERVICE_BASE_URL), {
        credentials: getRequestCredentials(
          env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
        ),
        method: "POST",
        body: { uuid, slices, slice, variation, html },
        okType: z.unknown(),
      }),
    schema: fitHtmlIntoSliceMessageSchema,
    onMessage: (message) => {
      onMessage(message)
      switch (message.status) {
        case "fitting":
          return { done: false }
        case "done":
        case "failed":
          return { done: true }
      }
    },
    onError: () => onMessage({ status: "failed" }),
  })
}

const fitHtmlIntoSliceMessageSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("fitting") }),
  z.object({
    status: z.literal("done"),
    slice: z.custom<SharedSliceContent>(isSharedSliceContent),
    cost: z.number(),
    distance: z.number(),
  }),
  z.object({ status: z.literal("failed") }),
])

type FitHtmlIntoSliceMessage = z.infer<typeof fitHtmlIntoSliceMessageSchema>
